<script setup>
import axios from 'axios'
import * as yup from 'yup'
import { computed, ref } from 'vue'
import { toTypedSchema } from '@vee-validate/yup'
import { Form, Field, ErrorMessage } from 'vee-validate'

const props = defineProps({
    endpoint: {
        type: String,
        default: '/api/subscribe',
    },
    source: {
        type: String,
        default: 'subscribe-widget',
    },
    buttonLabel: {
        type: String,
        default: 'Subscribe',
    },
    placeholder: {
        type: String,
        default: 'Enter your email',
    },
    label: {
        type: String,
        default: 'Email address',
    },
    successMessage: {
        type: String,
        default: '',
    },
})

const schema = toTypedSchema(yup.object({
    email: yup.string().email('Enter a valid email address').required('Email is required'),
}))

const isSubmitting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const isSubmitted = ref(false)

const resolvedButtonLabel = computed(() => {
    return props.buttonLabel && props.buttonLabel.trim() !== '' ? props.buttonLabel.trim() : 'Subscribe'
})

const resolvedPlaceholder = computed(() => {
    return props.placeholder && props.placeholder.trim() !== '' ? props.placeholder.trim() : 'Enter your email'
})

const resolvedLabel = computed(() => {
    return props.label && props.label.trim() !== '' ? props.label.trim() : 'Email address'
})

async function submit(values, { resetForm }) {
    if (isSubmitting.value) {
        return
    }

    errorMessage.value = ''
    successMessage.value = ''
    isSubmitted.value = false
    isSubmitting.value = true

    try {
        const { data } = await axios.post(props.endpoint, {
            email: values.email,
            source: props.source,
        })

        if (data.success !== true) {
            errorMessage.value = data.message || 'Unable to process your subscription right now.'
            return
        }

        successMessage.value = props.successMessage && props.successMessage.trim() !== ''
            ? props.successMessage.trim()
            : (data.message || 'Thanks for subscribing.')

        resetForm()
        isSubmitted.value = true
    } catch (error) {
        errorMessage.value = error.response?.data?.message || 'Unable to process your subscription right now.'
    } finally {
        isSubmitting.value = false
    }
}
</script>

<template>
    <div class="subscribe-form-section__form-area">
        <Form class="subscribe-form" :validation-schema="schema" @submit="submit" novalidate>
            <div class="text-center" v-if="isSubmitted && successMessage">
                <h3>Thank you!</h3>
                <p>{{ successMessage }}</p>
            </div>

            <div class="row gy-1" v-if="!isSubmitted">
                <div class="col-12 form-group">
                    <label class="form-label" for="subscribe-form-email">{{ resolvedLabel }} *</label>
                    <Field name="email" v-slot="{ field }">
                        <input
                            id="subscribe-form-email"
                            v-bind="field"
                            type="email"
                            class="form-control"
                            :placeholder="resolvedPlaceholder"
                            autocomplete="email"
                            inputmode="email"
                            aria-describedby="subscribe-form-message"
                        />
                    </Field>
                </div>
                <div class="col-12 form-group">
                    <button
                        type="submit"
                        class="btn btn-lg btn-block btn-rounded"
                        :disabled="isSubmitting"
                    >
                        {{ isSubmitting ? 'Submitting...' : resolvedButtonLabel }}
                    </button>

                    <ErrorMessage name="email" v-slot="{ message }">
                        <div v-if="message || errorMessage" id="subscribe-form-message" class="mt-3">
                            <div v-if="errorMessage" class="invalid-feedback d-block">
                                {{ errorMessage }}
                            </div>

                            <div v-else class="invalid-feedback d-block">
                                {{ message }}
                            </div>
                        </div>
                    </ErrorMessage>
                </div>
            </div>
        </Form>
    </div>
</template>
